import { useMemo } from "react";
import type { DecimalString } from "@/lib/decimal";
import { balanceStore, type BalanceStore } from "./balanceStore";
import { useBalance } from "./useBalance";

const CAPTURE_TIMEOUT_MS = 15_000;

/** Actively requests a fresh stable weight; keyboard mode waits for the next PRINT, never the previous one. */
export function waitForStableReading(
  store: BalanceStore = balanceStore,
  timeoutMs = CAPTURE_TIMEOUT_MS,
): Promise<DecimalString> {
  return store.captureStableReading(timeoutMs).then((reading) => reading.grams!);
}

/**
 * The balance as a "read the weight" source for forms (matches `WeightCapture` in
 * `@/features/inventory/api`): null while no balance is connected, so forms show no balance button.
 */
export interface BalanceWeightSource {
  label: string;
  capture: () => Promise<DecimalString>;
  ready: boolean;
  status?: string;
}

export function useBalanceWeightSource(): BalanceWeightSource | null {
  const balance = useBalance();
  const { status, stable, identity, transport } = balance;
  return useMemo(() => {
    if (status === "disconnected" || status === "error") return null;
    return {
      label: `Read ${identity.model ?? "balance"}`,
      capture: () => waitForStableReading(),
      ready: status === "connected",
      status:
        status !== "connected"
          ? "Reconnecting"
          : transport === "keyboard"
            ? "Press PRINT for a new reading"
            : stable
              ? "Stable"
              : "Settling",
    };
  }, [status, stable, identity.model, transport]);
}
